import { ReactNode, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMockAuth } from '../../hooks/useMockAuth';

type UserRole = 'admin' | 'creator' | 'user';

interface RoleGuardProps {
  children: ReactNode;
  allowedRoles: UserRole[];
  redirectTo?: string;
  fallback?: ReactNode;
}

/**
 * RoleGuard - Protects routes based on user role
 * Redirects to specified path or shows fallback if user lacks permission
 *
 * Usage:
 * <RoleGuard allowedRoles={['admin', 'creator']}>
 *   <AdminOrCreatorContent />
 * </RoleGuard>
 *
 * With custom fallback:
 * <RoleGuard
 *   allowedRoles={['admin']}
 *   fallback={<AccessDenied />}
 * >
 *   <AdminOnlyContent />
 * </RoleGuard>
 */
export function RoleGuard({
  children,
  allowedRoles,
  redirectTo = '/',
  fallback
}: RoleGuardProps) {
  const { user, isLoading } = useMockAuth();
  const navigate = useNavigate();

  const hasPermission = user && allowedRoles.includes(user.role);

  useEffect(() => {
    // Only redirect if explicitly requested (no fallback provided)
    if (!isLoading && !hasPermission && !fallback) {
      console.warn(`Access denied: User role "${user?.role}" not in allowed roles [${allowedRoles.join(', ')}]`);
      navigate({ to: redirectTo });
    }
  }, [user, isLoading, hasPermission, navigate, redirectTo, allowedRoles, fallback]);

  // Show loading state
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '89.7vh',
        fontSize: '1.125rem',
        color: '#6b7280'
      }}>
        Loading...
      </div>
    );
  }

  // User doesn't have permission
  if (!hasPermission) {
    // Show fallback if provided
    if (fallback) {
      return <>{fallback}</>;
    }

    // Otherwise don't render anything (redirect will happen via useEffect)
    return null;
  }

  return <>{children}</>;
}

/**
 * Permission-based convenience guards
 */

export function AdminGuard({ children, fallback }: Omit<RoleGuardProps, 'allowedRoles'>) {
  return (
    <RoleGuard allowedRoles={['admin']} fallback={fallback}>
      {children}
    </RoleGuard>
  );
}

export function CreatorGuard({ children, fallback }: Omit<RoleGuardProps, 'allowedRoles'>) {
  return (
    <RoleGuard allowedRoles={['admin', 'creator']} fallback={fallback}>
      {children}
    </RoleGuard>
  );
}

/**
 * AccessDenied - Default fallback component for unauthorized access
 */
export function AccessDenied() {
  const navigate = useNavigate();
  const { user } = useMockAuth();

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '89.7vh',
      padding: '2rem',
      textAlign: 'center'
    }}>
      <div style={{
        maxWidth: '32rem',
        padding: '2rem',
        borderRadius: '0.5rem',
        backgroundColor: '#fef2f2',
        border: '1px solid #fecaca'
      }}>
        <h1 style={{
          fontSize: '1.875rem',
          fontWeight: 'bold',
          color: '#991b1b',
          marginBottom: '1rem'
        }}>
          Access Denied
        </h1>
        <p style={{
          fontSize: '1rem',
          color: '#7f1d1d',
          marginBottom: '1.5rem'
        }}>
          You don't have permission to access this page.
          {user && (
            <span style={{ display: 'block', marginTop: '0.5rem' }}>
              Current role: <strong>{user.role}</strong>
            </span>
          )}
        </p>
        <button
          onClick={() => navigate({ to: '/' })}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#dc2626',
            color: 'white',
            borderRadius: '0.375rem',
            border: 'none',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '500'
          }}
        >
          Go to Home
        </button>
      </div>
    </div>
  );
}
